<script setup lang="ts">
import {
  computed,
  h,
  onMounted,
  onUnmounted,
  ref,
  useAttrs,
  watch,
} from 'vue';
import { useUtilities } from '@apparatix/composables';

const MEASUREMENT_HOST_ID = 'apx-text-truncate-measurement-host';

export type TextTruncateTextElement = 'a' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'li' | 'p' | 'span';

type Props = {
  text: string,
  /**
   * Array of classes the truncated text will use. Each class should be an individual array item
   * @default undefined
   */
  textClasses?: string[],
  /**
   * 'word' will truncate the text so that a full word is always visible at the end
   * 'exact' will fit exactly as many characters as there is horizontal space for
   * @default 'exact'
   */
  breakStrategy?: 'word' | 'exact';
  /**
   * Whether or not to show the … character at the end of truncated text. This is only shown if the text is truncated
   * @default true
   */
  showEllipsis?: boolean,
  /**
   * html element to use to contain the text
   * @default 'span'
   */
  element?: TextTruncateTextElement,
  /**
   * href for when 'element' is 'a'
   * @default undefined
   */
  href?: string,
  /**
   * Always show at least this many characters, even if it will cause overflow. This prop is unused when breakStrategy is 'word'
   * @default 3
   */
  minCharacters?: number,
};

type Emits = {
  (e: 'truncated', didTruncate: boolean): void,
};

const props = withDefaults(defineProps<Props>(), {
  textClasses: undefined,
  breakStrategy: 'exact',
  showEllipsis: true,
  element: 'span',
  href: undefined,
  minCharacters: 3,
});

const emit = defineEmits<Emits>();

const attrs = useAttrs();

const { developerWarning } = useUtilities();

const container = ref<HTMLDivElement>();
const observer = ref<ResizeObserver>();
const truncatedText = ref<string>();

/**
 * Callers can style the text either via the textClasses prop or via a plain class attribute
 * (which Vue applies to the root div, whose font styles the text inherits). The off-DOM
 * measurement element must carry both, or text measured at default styling gets under- or
 * over-truncated relative to what actually renders.
 */
const measurementClasses = computed<string[]>(() => {
  const bound = attrs.class;
  const boundClasses = typeof bound === 'string' ? bound.split(/\s+/) : [];

  return [...boundClasses, ...(props.textClasses ?? [])].filter(Boolean);
});

watch(() => props.text, newText => {
  if (!newText) {
    truncatedText.value = undefined;
    return;
  }

  const suffixedNewText = `${newText}${props.showEllipsis ? '…' : ''}`;

  // If incoming text length is shorter than what's already visible, there's no point in re-running the size calculations
  if (truncatedText.value && suffixedNewText.length <= truncatedText.value.length) {
    truncatedText.value = suffixedNewText;
    return;
  }

  // Before mount there's nothing to measure against; the ResizeObserver truncates on mount
  if (!container.value) {
    return;
  }

  const { width } = container.value.getBoundingClientRect();
  truncate(width);
});

/**
 * Off-screen, hidden host for the measurement element. A regular document node rather
 * than a shadow root: document stylesheets don't match elements inside shadow trees,
 * so classes added there (Tailwind utilities) would have no effect on measurement.
 */
const getMeasurementHost = () => {
  const existing = document.getElementById(MEASUREMENT_HOST_ID);
  if (existing) {
    return existing;
  }

  const host = document.createElement('div');
  host.id = MEASUREMENT_HOST_ID;
  host.style.position = 'absolute';
  host.style.top = '-9999px';
  host.style.left = '-9999px';
  host.style.visibility = 'hidden';
  document.body.appendChild(host);

  return host;
};

const truncate = (containerWidth: number) => {
  const host = getMeasurementHost();

  const tempTextEl = document.createElement(props.element);
  tempTextEl.style.textWrap = 'nowrap';
  tempTextEl.style.display = 'inline-block';
  measurementClasses.value.forEach(c => {
    try {
      tempTextEl.classList.add(c);
    } catch {
      developerWarning(
        true,
        `Invalid class passed to TextTruncate: '${c}'.\nThis is most likely due to spaces in the class (eg ['text-base font-bold'] instead of ['text-base', 'font-bold']), or passing a falsy value`,
        'console:warn',
      );
    }
  });
  host.appendChild(tempTextEl);

  const suffix = props.showEllipsis ? '…' : '';

  if (props.breakStrategy === 'exact') {
    let tempText = '';
    for (let i=0; i<props.text.length; i++) {
      tempText = `${tempText}${props.text[i]}`;
      tempTextEl.innerHTML = `${tempText}${suffix}`;

      const { width: textWidth } = tempTextEl.getBoundingClientRect();

      if (textWidth > containerWidth) {
        const truncated = `${props.text.substring(0, i - 1).trimEnd()}`;

        // Ending with a period looks weird with an ellipsis.…
        if (truncated.endsWith('.') && props.showEllipsis) {
          truncatedText.value = `${truncated.substring(0, truncated.length - 2)}${suffix}`; // -2 because length is already off by one
        } else {
          truncatedText.value = `${truncated}${suffix}`;
        }

        break;
      }
    }
  } else {
    const words = props.text.split(/\s/);
    for (let i=0; i<words.length; i++) {
      const currentText = `${words.slice(0, i).join(' ')}`;
      tempTextEl.innerHTML = `${currentText}${suffix}`;

      const { width: textWidth } = tempTextEl.getBoundingClientRect();

      if (textWidth > containerWidth) {
        truncatedText.value = `${words.slice(0, i - 1).join(' ')}${suffix}`;
        break;
      }
    }
  }

  if (!truncatedText.value) {
    truncatedText.value = props.text;
    emit('truncated', false);
  } else {
    emit('truncated', true);
  }

  host.removeChild(tempTextEl);
};

const resizeCallback: ResizeObserverCallback = entries => {
  const [entry] = entries;
  if (!entry?.target?.checkVisibility()) {
    return;
  }

  truncate(entry.contentRect.width);
};

const renderTextNode = () => {
  const textProps: any = {
    class: props.textClasses,
  };

  if (props.element === 'a' && props.href) {
    textProps.href = props.href;
    textProps.target = '_blank';
  }

  return () => h(props.element, { ...textProps }, truncatedText.value);
};

onMounted(() => {
  observer.value = new ResizeObserver(resizeCallback);
  observer.value.observe(container.value);
});

onUnmounted(() => observer.value?.disconnect());
</script>
<template>
  <div ref="container" class="w-full overflow-x-hidden">
    <component :is="renderTextNode()" />
  </div>
</template>
